import type React from "react";
import { useCallback, useContext, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useMatch, useNavigate } from "react-router-dom";

import type { JointOrAilment, Patients } from "api/schemas/Patients";
import { FilterDrawer } from "shared/atoms/FilterDrawer";
import { PatientsFilterContext } from "utils/contexts";
import { useQueryParams } from "utils/hooks";
import { joinUrl } from "utils/url/joinUrl";

import { createFilterSections } from "./createFilterSections";
import { getTreatmentId } from "./stringifyJoint";
import { useFilterOptions } from "./useFilterOptions";
import type { Filters } from "./useFilters";
import { useWeekFilter } from "./useWeekFilter";

type PatientsFilterProps = {
  close: () => void;
  open: boolean;
  patients: Patients[];
};

export const PatientsFilter: React.FC<PatientsFilterProps> = ({ open, close, patients }) => {
  const { t } = useTranslation();
  const { clearAll } = useContext<Filters>(PatientsFilterContext);

  const params = useQueryParams();
  const navigate = useNavigate();
  const match = useMatch("*");

  const uniqueTreatments = useMemo(() => getUniqueTreatments(patients), [patients]);

  const { fromInput, toInput, handleWeekInput } = useWeekFilter();

  const {
    createOptions,
    getSelected,
    handleMultiSelectChange,
    statusCheckboxes,
    tasks,
    taskOptions,
    categories,
    membershipStatuses,
    assignmentRoles,
    assignmentRolesPhrases,
    ailmentOptions,
    primaryJointState,
    setFilter,
  } = useFilterOptions(uniqueTreatments);

  const onClearAll = useCallback(() => {
    clearAll();
    params.delete("filter");
    if (match) navigate(joinUrl(match?.pathname, params));
  }, [clearAll, params, navigate, match?.pathname]);

  const filterSections = createFilterSections({
    t,
    tasks,
    taskOptions,
    getSelected,
    handleMultiSelectChange,
    statusCheckboxes,
    fromInput,
    toInput,
    handleWeekInput,
    ailmentOptions,
    primaryJointSelection: primaryJointState.selection || [],
    setFilter,
    categories,
    createOptions,
    membershipStatuses,
    assignmentRoles,
    assignmentRolesPhrases,
  });

  return (
    <FilterDrawer
      open={open}
      onClose={close}
      title={t("patients.filters")}
      sections={filterSections}
      onClear={onClearAll}
    />
  );
};

const getUniqueTreatments = (patients: Patients[]): JointOrAilment[] => {
  const uniqueTreatments = patients.reduce<Record<string, JointOrAilment>>((accumulator, patient) => {
    const { ailment, primary_joint } = patient;
    const key = getTreatmentId({
      ailment,
      joint: primary_joint || { location: null, lateral_location: null },
    });

    accumulator[key] = {
      ailment,
      joint: primary_joint || { location: null, lateral_location: null },
    };

    return accumulator;
  }, {});

  return Object.values(uniqueTreatments).sort((a, b) => {
    const aIsAilment = a.ailment !== "joint_pain";
    const bIsAilment = b.ailment !== "joint_pain";

    if (aIsAilment && bIsAilment) return a.ailment.localeCompare(b.ailment);
    if (aIsAilment) return 1;
    if (bIsAilment) return -1;

    const aLocation = a.joint.location || "";
    const bLocation = b.joint.location || "";
    if (aLocation !== bLocation) return aLocation.localeCompare(bLocation);

    return (a.joint.lateral_location || "").localeCompare(b.joint.lateral_location || "");
  });
};
